

import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations/translations';
import './BemvindoSection.css';

function BemvindoSection() {
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const { language } = useLanguage();
    const t = translations?.[language] || translations?.pt || {};

    useEffect(() => {
        // detect mobile viewport to skip per-letter splitting on small screens
        const mq = window.matchMedia('(max-width: 768px)');
        const handleMq = (e) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', handleMq);
        else mq.addListener(handleMq);

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Stop observing once visible to avoid resetting
                    observer.disconnect();
                }
            },
            { threshold: 0.4 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const splitTextIntoSpans = (text, startIndex = 0) => {
        if (isMobile) {
            // On mobile, render the full string to avoid per-letter animation and inline delays
            return text;
        }

        let delayIndex = startIndex;

        return text.split(/(\s+)/).map((part, partIndex) => {
            if (!part) return null;

            // Preserve spacing between words while allowing line breaks only at spaces.
            if (/^\s+$/.test(part)) {
                return (
                    <span key={`space-${startIndex}-${partIndex}`}>
                        {part.replace(/\s/g, '\u00A0')}
                    </span>
                );
            }

            return (
                <span key={`word-${startIndex}-${partIndex}`} className="letter-word">
                    {part.split('').map((char) => {
                        const currentDelayIndex = delayIndex++;
                        return (
                            <span
                                key={`char-${currentDelayIndex}`}
                                className={`letter ${isVisible ? 'animate' : ''}`}
                                style={{ transitionDelay: `${currentDelayIndex * 0.02}s` }}
                            >
                                {char}
                            </span>
                        );
                    })}
                </span>
            );
        });
    };

    return (
        <section
            ref={sectionRef}
            className="bemvindo-section"
        >
            <div className="bemvindo-content">
                <h1 className="main-title">
                    {splitTextIntoSpans(t.bemvindo.line1, 0)}
                </h1>
                <h1 className="main-title secondary">
                    {splitTextIntoSpans(t.bemvindo.line2, t.bemvindo.line1.length)}
                </h1>
                <h1 className="main-title tertiary">
                    {splitTextIntoSpans(t.bemvindo.line3, t.bemvindo.line1.length + t.bemvindo.line2.length)}
                </h1>
                <h1 className="main-title quaternary">
                    {splitTextIntoSpans(t.bemvindo.line4, t.bemvindo.line1.length + t.bemvindo.line2.length + t.bemvindo.line3.length)}
                </h1>

                <div className="subtitle-container">
                    <p className={`subtitle ${isVisible ? 'fade-in' : ''}`}>
                        {t.bemvindo.subtitle1}
                        <br />
                        {t.bemvindo.subtitle2}
                    </p>
                </div>
            </div>
        </section>
    );
}

export default BemvindoSection;